<script setup lang="ts">
import 'leaflet/dist/leaflet.css'
import { LMap } from '#components'

const props = withDefaults(defineProps<{ items?: Business[], zoom?: number, large?: boolean, distance?: number }>(), { zoom: 11 })

const mapRef = ref<typeof LMap>()

const centerPoint = ref([57.10102407645874, -2.6186201928452175])

const transformedItems = computed(() => {
  if (!props.items)
    return []
  return props.items?.filter(x => x.map?.lat && x.map?.lng)
})
const circle = ref({
  radius: 1000,
  visible: false,
})

watch(() => [props.items, props.distance], async () => {
  setMap()
})

const mapIsLoaded = ref(false)

function setupMap() {
  mapIsLoaded.value = true
  setMap()
  mapRef.value.leafletObject.invalidateSize()
}

function setMap() {
  centerPoint.value = getCenter()
  if (props.distance) {
    // props.distance in in miles. Convert to meters
    const distanceInMeters = props.distance * 1609.34 // Convert miles to meters
    circle.value = {
      radius: distanceInMeters,
      visible: true,
    }
  }
  else {
    circle.value.visible = false
  }
}

function getCenter() {
  if (!transformedItems.value?.length)
    return [57.10102407645874, -2.6186201928452175]
  let lat = 0
  let lng = 0
  for (let i = 0; i < transformedItems.value.length; i++) {
    lat += transformedItems.value?.[i]?.map?.lat ?? 0
    lng += transformedItems.value?.[i]?.map?.lng ?? 0
  }
  lat = lat / transformedItems.value.length
  lng = lng / transformedItems.value.length
  return [lat, lng]
}

const animDone = ref('')

onMounted(() => {
  setTimeout(() => {
    animDone.value = 'animation-complete'
  }, 3000)
})

const mapIsVisible = useElementVisibility(mapRef, { threshold: 0.5 })
watch(mapIsVisible, (val) => {
  if (!val) {
    animDone.value = ''
  }
})

watch(mapIsLoaded, (val) => {
  setTimeout(() => {
    mapRef.value.leafletObject.invalidateSize()
  }, 1000)
}, {
  immediate: true,
})

function viewItem(business: Business) {
  const { id = '' } = useBusiness(business)
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
</script>

<template>
  <div class="relative w-full">
    <NuxtErrorBoundary>
      <LMap
        ref="mapRef"
        :zoom="zoom"
        :center="centerPoint"
        class="map-wrapper"
        :attribution-control="false"
        :use-global-leaflet="false"
        @ready="setupMap"
      >
        <LControlZoom position="bottomleft" />
        <LTileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          layer-type="base"
          name="OpenStreetMap"
          :attribution-control="false"
        />
        <template v-if="mapIsVisible && mapIsLoaded">
          <LMarker
            v-for="(item, index) in transformedItems"
            :key="item.id"
            :lat-lng="[item.map.lat, item.map.lng]"
            @click="viewItem(item)"
          >
            <LIcon
              icon-url="/Icon.png"
              :icon-size="[55, 45]"
              :class-name="`drop-in drop-in-${index + 1} ${animDone}`"
            />
          </LMarker>
        </template>
        <LCircle
          v-if="circle.visible"
          :lat-lng="centerPoint"
          :radius="circle.radius"
          color="#0065B2"
          fill-color="#009AFF"
          :fill-opacity="0.1"
          :weight="1"
        />
      </LMap>
    </NuxtErrorBoundary>
  </div>
</template>

<style>
.leaflet-control-attribution.leaflet-control {
  display: none;
}

.map-wrapper {
  width: 100%;
  height: 600px !important;
  max-height: 80vh;
  z-index: 2;
}

.largemap .map-wrapper {
  width: 100%;
  height: 900px !important;
  max-height: 80vh;
}

.largemap {
  height: 2000px !important;
  max-height: 80vh;
}

@keyframes drop {
  from {
    opacity: 0;
    transform: translateY(-100px);
  }

  to {
    opacity: 1;
  }
}

.drop-in {
  animation-name: drop;
  animation-timing-function: cubic-bezier(0.075, 0.82, 0.165, 1);
  animation-direction: normal;
  animation-duration: 1s;
}

.drop-in.drop-in-1 {
  animation-duration: 1s;
}

.drop-in.drop-in-2 {
  animation-duration: 1.1s;
}

.drop-in.drop-in-3 {
  animation-duration: 1.2s;
}

.drop-in.drop-in-4 {
  animation-duration: 1.6s;
}

.drop-in.drop-in-5 {
  animation-duration: 1.8s;
}

.drop-in.animation-complete {
  animation-duration: 0s;
}
</style>
